import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, Calendar, Clock, Scale, Euro, FileText, 
  ArrowRight, Loader2, Car, RotateCw, Plane
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { tripsApi } from '../../lib/api';
import { toast } from 'sonner';
import AddressAutocomplete from '../../components/AddressAutocomplete';

const NewTripPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    departure_city: '',
    departure_address: '',
    departure_postal_code: '',
    arrival_city: '',
    arrival_address: '',
    arrival_postal_code: '',
    departure_date: '',
    departure_time: '',
    available_weight: '',
    available_volume: '',
    price_per_kg: '',
    description: '',
    recurrent: false,
    recurrent_days: [],
    transport_mode: 'road',
    airline: '',
    flight_number: '',
    departure_airport: '',
    arrival_airport: '',
  });

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // Handle departure address selection from autocomplete
  const handleDepartureSelect = (addressData) => {
    if (addressData) {
      setFormData(prev => ({
        ...prev,
        departure_address: addressData.street || addressData.formatted_address || '',
        departure_city: addressData.city || '',
        departure_postal_code: addressData.postal_code || ''
      }));
    }
  };

  // Handle arrival address selection from autocomplete
  const handleArrivalSelect = (addressData) => {
    if (addressData) {
      setFormData(prev => ({
        ...prev,
        arrival_address: addressData.street || addressData.formatted_address || '',
        arrival_city: addressData.city || '',
        arrival_postal_code: addressData.postal_code || ''
      }));
    }
  };

  const toggleDay = (day) => {
    const days = formData.recurrent_days.includes(day)
      ? formData.recurrent_days.filter(d => d !== day)
      : [...formData.recurrent_days, day];
    setFormData({ ...formData, recurrent_days: days });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const tripData = {
        departure_city: formData.departure_city,
        departure_address: formData.departure_address,
        departure_postal_code: formData.departure_postal_code,
        arrival_city: formData.arrival_city,
        arrival_address: formData.arrival_address,
        arrival_postal_code: formData.arrival_postal_code,
        departure_date: formData.departure_date,
        departure_time: formData.departure_time,
        available_weight: parseFloat(formData.available_weight),
        available_volume: formData.available_volume ? parseFloat(formData.available_volume) : null,
        price_per_kg: parseFloat(formData.price_per_kg),
        description: formData.description,
        recurrent: formData.recurrent,
        recurrent_days: formData.recurrent ? formData.recurrent_days : null,
        transport_mode: formData.transport_mode,
        airline: formData.transport_mode === 'air' ? formData.airline : null,
        flight_number: formData.transport_mode === 'air' ? formData.flight_number : null,
        departure_airport: formData.transport_mode === 'air' ? formData.departure_airport : null,
        arrival_airport: formData.transport_mode === 'air' ? formData.arrival_airport : null,
      };

      await tripsApi.create(tripData);
      toast.success('Trajet publié avec succès!');
      navigate('/dashboard/my-trips');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const weekDays = [
    { key: 'monday', label: 'Lun' },
    { key: 'tuesday', label: 'Mar' },
    { key: 'wednesday', label: 'Mer' },
    { key: 'thursday', label: 'Jeu' },
    { key: 'friday', label: 'Ven' },
    { key: 'saturday', label: 'Sam' },
    { key: 'sunday', label: 'Dim' },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fade-in" data-testid="new-trip-page">
      <div>
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
          Publier un trajet
        </h1>
        <p className="text-slate-600 mt-1">
          Publiez votre trajet et gagnez de l'argent en transportant des colis
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Transport Mode */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase flex items-center gap-2">
              Mode de transport
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => updateField('transport_mode', 'road')}
                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  formData.transport_mode === 'road'
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-slate-200 hover:border-slate-300 text-slate-600'
                }`}
                data-testid="transport-mode-road"
              >
                <Car className="w-8 h-8" />
                <span className="font-semibold">Routier</span>
                <span className="text-xs opacity-70">Transport par route</span>
              </button>
              <button
                type="button"
                onClick={() => updateField('transport_mode', 'air')}
                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  formData.transport_mode === 'air'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-slate-200 hover:border-slate-300 text-slate-600'
                }`}
                data-testid="transport-mode-air"
              >
                <Plane className="w-8 h-8" />
                <span className="font-semibold">Fret aérien</span>
                <span className="text-xs opacity-70">Transport par avion</span>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Air Freight Details */}
        {formData.transport_mode === 'air' && (
          <Card className="border-blue-200 bg-blue-50/30">
            <CardHeader>
              <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-blue-900 uppercase flex items-center gap-2">
                <Plane className="w-5 h-5 text-blue-500" />
                Détails du vol
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="airline">Compagnie aérienne</Label>
                  <Input
                    id="airline"
                    placeholder="Air France, Ryanair..."
                    value={formData.airline}
                    onChange={(e) => updateField('airline', e.target.value)}
                    data-testid="airline-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="flight_number">Numéro de vol</Label>
                  <Input
                    id="flight_number"
                    placeholder="AF1234"
                    value={formData.flight_number}
                    onChange={(e) => updateField('flight_number', e.target.value)}
                    data-testid="flight-number-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="departure_airport">Aéroport de départ</Label>
                  <Input
                    id="departure_airport"
                    placeholder="CDG, ORY, LYS..."
                    value={formData.departure_airport}
                    onChange={(e) => updateField('departure_airport', e.target.value)}
                    data-testid="departure-airport-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="arrival_airport">Aéroport d'arrivée</Label>
                  <Input
                    id="arrival_airport"
                    placeholder="MRS, NCE, TLS..."
                    value={formData.arrival_airport}
                    onChange={(e) => updateField('arrival_airport', e.target.value)}
                    data-testid="arrival-airport-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Departure */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase flex items-center gap-2">
              <MapPin className="w-5 h-5 text-green-500" />
              Point de départ
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Rechercher une adresse de départ</Label>
              <AddressAutocomplete
                placeholder="Tapez une adresse ou une ville..."
                onAddressSelect={handleDepartureSelect}
                data-testid="departure-autocomplete"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2 md:col-span-1">
                <Label htmlFor="departure_address">Adresse</Label>
                <Input
                  id="departure_address"
                  placeholder="123 rue de la Gare"
                  value={formData.departure_address}
                  onChange={(e) => updateField('departure_address', e.target.value)}
                  data-testid="departure-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="departure_city">Ville *</Label>
                <Input
                  id="departure_city"
                  placeholder="Paris"
                  value={formData.departure_city}
                  onChange={(e) => updateField('departure_city', e.target.value)}
                  required
                  data-testid="departure-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="departure_postal_code">Code postal</Label>
                <Input
                  id="departure_postal_code"
                  placeholder="75001"
                  value={formData.departure_postal_code}
                  onChange={(e) => updateField('departure_postal_code', e.target.value)}
                  data-testid="departure-postal-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Arrival */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase flex items-center gap-2">
              <MapPin className="w-5 h-5 text-red-500" />
              Point d'arrivée
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Rechercher une adresse d'arrivée</Label>
              <AddressAutocomplete
                placeholder="Tapez une adresse ou une ville..."
                onAddressSelect={handleArrivalSelect}
                data-testid="arrival-autocomplete"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2 md:col-span-1">
                <Label htmlFor="arrival_address">Adresse</Label>
                <Input
                  id="arrival_address"
                  placeholder="456 avenue du Centre"
                  value={formData.arrival_address}
                  onChange={(e) => updateField('arrival_address', e.target.value)}
                  data-testid="arrival-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="arrival_city">Ville *</Label>
                <Input
                  id="arrival_city"
                  placeholder="Lyon"
                  value={formData.arrival_city}
                  onChange={(e) => updateField('arrival_city', e.target.value)}
                  required
                  data-testid="arrival-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="arrival_postal_code">Code postal</Label>
                <Input
                  id="arrival_postal_code"
                  placeholder="69001"
                  value={formData.arrival_postal_code}
                  onChange={(e) => updateField('arrival_postal_code', e.target.value)}
                  data-testid="arrival-postal-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Date & Time */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Date et heure
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="departure_date">Date de départ</Label>
                <Input
                  id="departure_date"
                  type="date"
                  value={formData.departure_date}
                  onChange={(e) => updateField('departure_date', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  data-testid="departure-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="departure_time" className="flex items-center gap-1">
                  <Clock className="w-4 h-4 text-slate-400" />
                  Heure de départ
                </Label>
                <Input
                  id="departure_time"
                  type="time"
                  value={formData.departure_time}
                  onChange={(e) => updateField('departure_time', e.target.value)}
                  required
                  data-testid="departure-time-input"
                />
              </div>
            </div>

            {/* Recurrent Trip */}
            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center space-x-2 mb-4">
                <Checkbox
                  id="recurrent"
                  checked={formData.recurrent}
                  onCheckedChange={(checked) => updateField('recurrent', checked)}
                  data-testid="recurrent-checkbox"
                />
                <Label htmlFor="recurrent" className="flex items-center gap-2 cursor-pointer">
                  <RotateCw className="w-4 h-4 text-orange-500" />
                  Trajet régulier (je fais ce trajet plusieurs fois par semaine)
                </Label>
              </div>

              {formData.recurrent && (
                <div className="space-y-2 pl-6">
                  <Label>Jours de la semaine</Label>
                  <div className="flex flex-wrap gap-2">
                    {weekDays.map((day) => (
                      <button
                        key={day.key}
                        type="button"
                        onClick={() => toggleDay(day.key)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          formData.recurrent_days.includes(day.key)
                            ? 'bg-orange-500 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        data-testid={`day-${day.key}`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Capacity & Price */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase flex items-center gap-2">
              <Car className="w-5 h-5 text-purple-500" />
              Capacité et tarif
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="available_weight" className="flex items-center gap-1">
                  <Scale className="w-4 h-4 text-slate-400" />
                  Poids max (kg)
                </Label>
                <Input
                  id="available_weight"
                  type="number"
                  step="0.1"
                  placeholder="20"
                  value={formData.available_weight}
                  onChange={(e) => updateField('available_weight', e.target.value)}
                  required
                  data-testid="weight-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="available_volume">Volume max (m³) - optionnel</Label>
                <Input
                  id="available_volume"
                  type="number"
                  step="0.01"
                  placeholder="0.5"
                  value={formData.available_volume}
                  onChange={(e) => updateField('available_volume', e.target.value)}
                  data-testid="volume-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price_per_kg" className="flex items-center gap-1">
                  <Euro className="w-4 h-4 text-slate-400" />
                  Prix par kg (€)
                </Label>
                <Input
                  id="price_per_kg"
                  type="number"
                  step="0.5"
                  placeholder="2.5"
                  value={formData.price_per_kg}
                  onChange={(e) => updateField('price_per_kg', e.target.value)}
                  required
                  data-testid="price-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="flex items-center gap-1">
                <FileText className="w-4 h-4 text-slate-400" />
                Description (optionnel)
              </Label>
              <Textarea
                id="description"
                placeholder="Informations supplémentaires sur votre trajet, type de véhicule, flexibilité horaire..."
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                className="min-h-[80px]"
                data-testid="description-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Info Box */}
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Euro className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Comment ça marche ?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Les expéditeurs verront votre trajet et pourront vous contacter pour transporter leurs colis.
                  Vous fixez votre prix par kg. Une commission de 15% sera prélevée sur chaque transaction.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="border-slate-300"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide px-8"
            data-testid="submit-trip-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Publication...
              </>
            ) : (
              <>
                Publier le trajet
                <ArrowRight className="ml-2 w-5 h-5" />
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NewTripPage;
